import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von08Neurology } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von08NeurologyFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von08NeurologyForm: React.FC<Von08NeurologyFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  const [pihImaging, setPihImaging] = useState(false);
  const [pihGrade, setPihGrade] = useState<string>('');
  const [pihWhere, setPihWhere] = useState<string>('');
  const [pvl, setPvl] = useState<string>('');
  const [ventriculitisAny, setVentriculitisAny] = useState(false);
  const [hydrocephalusAny, setHydrocephalusAny] = useState(false);
  const [seizures, setSeizures] = useState(false);

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getNeurology(patientId);

      if (data) {
        setHasExistingData(true);
        setPihImaging(Boolean(data.pih_imaging));
        setPihGrade(data.pih_grade?.toString() || '');
        setPihWhere(data.pih_where?.toString() || '');
        setPvl(data.pvl?.toString() || '');
        setVentriculitisAny(Boolean(data.ventriculitis_any));
        setHydrocephalusAny(Boolean(data.hydrocephalus_any));
        setSeizures(Boolean(data.seizures));
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von08Neurology> = {
        patient_id: patientId,
        pih_imaging: pihImaging,
        pih_grade: pihGrade ? parseInt(pihGrade) : undefined,
        pih_where: pihWhere ? parseInt(pihWhere) : undefined,
        pvl: pvl ? parseInt(pvl) : undefined,
        ventriculitis_any: ventriculitisAny,
        hydrocephalus_any: hydrocephalusAny,
        seizures: seizures
      };

      if (hasExistingData) {
        await vonDataService.updateNeurology(patientId, formData);
      } else {
        await vonDataService.createNeurology(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 08: Neurologie</CardTitle>
        <CardDescription>Date despre hemoragie intraventriculară și alte afecțiuni neurologice</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* IVH/PIH */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Hemoragie intraventriculară (IVH/PIH)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="pihImaging"
                  checked={pihImaging}
                  onCheckedChange={(checked) => setPihImaging(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="pihImaging" className="text-sm font-normal cursor-pointer">
                  Imagistică craniană {'<='} ziua 28 (US/CT/RMN)
                </Label>
              </div>

              {pihImaging && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="pihGrade">Cel mai rău grad PIH</Label>
                    <Select value={pihGrade} onValueChange={setPihGrade} disabled={readOnly}>
                      <SelectTrigger id="pihGrade">
                        <SelectValue placeholder="Selectează gradul..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Grad 0</SelectItem>
                        <SelectItem value="1">Grad 1</SelectItem>
                        <SelectItem value="2">Grad 2</SelectItem>
                        <SelectItem value="3">Grad 3</SelectItem>
                        <SelectItem value="4">Grad 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pihWhere">Unde a fost diagnosticat</Label>
                    <Select value={pihWhere} onValueChange={setPihWhere} disabled={readOnly}>
                      <SelectTrigger id="pihWhere">
                        <SelectValue placeholder="Selectează..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">NICU local</SelectItem>
                        <SelectItem value="2">NICU de transfer</SelectItem>
                        <SelectItem value="3">Ambele</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Other neurological conditions */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Alte condiții neurologice</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pvl">Leucomalacie periventriculară chistică (PVL)</Label>
                <Select value={pvl} onValueChange={setPvl} disabled={readOnly}>
                  <SelectTrigger id="pvl">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Da</SelectItem>
                    <SelectItem value="9">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ventriculitisAny"
                  checked={ventriculitisAny}
                  onCheckedChange={(checked) => setVentriculitisAny(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ventriculitisAny" className="text-sm font-normal cursor-pointer">
                  Ventriculită
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hydrocephalusAny"
                  checked={hydrocephalusAny}
                  onCheckedChange={(checked) => setHydrocephalusAny(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="hydrocephalusAny" className="text-sm font-normal cursor-pointer">
                  Hidrocefalie diagnosticată
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="seizures"
                  checked={seizures}
                  onCheckedChange={(checked) => setSeizures(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="seizures" className="text-sm font-normal cursor-pointer">
                  Convulsii
                </Label>
              </div>
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
